<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import ConversationTransferRequestsAPI from 'dashboard/api/aceleres/conversationTransferRequests';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';

const route = useRoute();
const router = useRouter();

const transferRequests = ref([]);
const transferRequestsLoading = ref(false);
const transferRequestsError = ref(false);
const expandedTransferRequestId = ref(null);
const decidingTransferRequestId = ref(null);
const rejectionTransferRequest = ref(null);
const rejectionReason = ref('');
const accountId = computed(() => Number(route.params.accountId));

let transferRequestFetchSequence = 0;
let transferRequestPollId = null;

const fetchTransferRequests = async () => {
  transferRequestFetchSequence += 1;
  const fetchSequence = transferRequestFetchSequence;
  transferRequestsLoading.value = true;

  try {
    const { data } = await ConversationTransferRequestsAPI.pending();
    if (fetchSequence !== transferRequestFetchSequence) return;

    transferRequests.value = data?.payload || [];
    transferRequestsError.value = false;
  } catch {
    if (fetchSequence === transferRequestFetchSequence) {
      transferRequestsError.value = true;
    }
  } finally {
    if (fetchSequence === transferRequestFetchSequence) {
      transferRequestsLoading.value = false;
    }
  }
};

const refreshTransferRequestsWhenVisible = () => {
  if (document.visibilityState === 'visible') fetchTransferRequests();
};

const toggleTransferRequestDetails = requestId => {
  expandedTransferRequestId.value =
    expandedTransferRequestId.value === requestId ? null : requestId;
};

const formatTransferRequestDate = createdAt => {
  if (!createdAt) return 'Data não informada';

  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(createdAt));
};

const openTransferConversation = request => {
  router.push({
    name: 'inbox_view_conversation',
    params: { type: 'conversation', id: request.conversation_id },
  });
};

const approveTransferRequest = async request => {
  decidingTransferRequestId.value = request.id;

  try {
    await ConversationTransferRequestsAPI.approve(request.id);
    useAlert('Alteração de carteirização aprovada');
    await fetchTransferRequests();
  } catch (error) {
    useAlert(error?.response?.data?.error || 'Não foi possível aprovar');
  } finally {
    decidingTransferRequestId.value = null;
  }
};

const openRejectTransferRequestModal = request => {
  rejectionTransferRequest.value = request;
  rejectionReason.value = '';
};

const closeRejectTransferRequestModal = () => {
  if (decidingTransferRequestId.value) return;

  rejectionTransferRequest.value = null;
  rejectionReason.value = '';
};

const rejectTransferRequest = async () => {
  const request = rejectionTransferRequest.value;
  if (!request) return;

  decidingTransferRequestId.value = request.id;

  try {
    await ConversationTransferRequestsAPI.reject(
      request.id,
      rejectionReason.value.trim()
    );
    useAlert('Alteração de carteirização rejeitada');
    rejectionTransferRequest.value = null;
    rejectionReason.value = '';
    await fetchTransferRequests();
  } catch (error) {
    useAlert(error?.response?.data?.error || 'Não foi possível rejeitar');
  } finally {
    decidingTransferRequestId.value = null;
  }
};

const onTransferRequestRealtimeUpdate = payload => {
  if (Number(payload?.account_id) !== accountId.value) return;

  if (
    expandedTransferRequestId.value === Number(payload?.request_id) &&
    payload?.status !== 'pending'
  ) {
    expandedTransferRequestId.value = null;
  }

  fetchTransferRequests();
};

onMounted(() => {
  emitter.on(
    BUS_EVENTS.ACELERES_CARTEIRIZACAO_REQUEST_UPDATED,
    onTransferRequestRealtimeUpdate
  );
  emitter.on(BUS_EVENTS.WEBSOCKET_RECONNECT, fetchTransferRequests);
  window.addEventListener('focus', fetchTransferRequests);
  document.addEventListener(
    'visibilitychange',
    refreshTransferRequestsWhenVisible
  );
  transferRequestPollId = window.setInterval(
    refreshTransferRequestsWhenVisible,
    10000
  );
  fetchTransferRequests();
});

onBeforeUnmount(() => {
  emitter.off(
    BUS_EVENTS.ACELERES_CARTEIRIZACAO_REQUEST_UPDATED,
    onTransferRequestRealtimeUpdate
  );
  emitter.off(BUS_EVENTS.WEBSOCKET_RECONNECT, fetchTransferRequests);
  window.removeEventListener('focus', fetchTransferRequests);
  document.removeEventListener(
    'visibilitychange',
    refreshTransferRequestsWhenVisible
  );
  window.clearInterval(transferRequestPollId);
});
</script>

<template>
  <section
    v-show="
      transferRequests.length ||
      transferRequestsError
    "
    data-testid="portfolio-transfer-requests-panel"
    class="shrink-0 border-b border-n-weak bg-n-solid-1 px-3 py-3"
  >
    <div class="mb-2 flex items-center justify-between gap-3">
      <div class="min-w-0">
        <p class="text-sm font-semibold text-n-slate-12">
          Alterações de carteirização pendentes
        </p>
        <p class="text-xs text-n-slate-10">
          {{ transferRequests.length }} aguardando sua decisão
        </p>
      </div>
      <button
        type="button"
        class="shrink-0 text-xs font-medium text-n-blue-11 hover:text-n-blue-12"
        :disabled="transferRequestsLoading"
        @click="fetchTransferRequests"
      >
        Atualizar
      </button>
    </div>

    <div
      v-if="transferRequestsError"
      class="mb-2 flex items-center justify-between gap-3 rounded-lg border border-n-ruby-5 bg-n-ruby-3 px-3 py-2"
    >
      <p class="text-xs text-n-ruby-11">
        Não foi possível atualizar. As solicitações carregadas foram
        preservadas.
      </p>
      <button
        type="button"
        class="shrink-0 text-xs font-semibold text-n-ruby-11 hover:text-n-ruby-12"
        @click="fetchTransferRequests"
      >
        Tentar novamente
      </button>
    </div>

    <div class="max-h-[45vh] space-y-2 overflow-y-auto pr-1">
      <article
        v-for="request in transferRequests"
        :key="request.id"
        class="rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2"
      >
        <button
          type="button"
          class="block w-full text-left"
          @click="openTransferConversation(request)"
        >
          <p class="truncate text-sm font-medium text-n-slate-12">
            #{{ request.conversation_id }}
            <span v-if="request.contact_name"
              >- {{ request.contact_name }}</span
            >
          </p>
          <p class="text-xs text-n-slate-10 break-words">
            {{ request.requester?.name }} solicitou
            {{ request.current_owner?.name || 'Sem dono definido' }} →
            {{ request.target_assignee?.name }}
          </p>
          <p v-if="request.team_name" class="text-xs text-n-slate-9">
            Equipe: {{ request.team_name }}
          </p>
          <p class="mt-2 line-clamp-3 text-xs text-n-slate-11 break-words">
            <span class="font-semibold">Justificativa:</span>
            {{ request.justification }}
          </p>
        </button>

        <button
          type="button"
          class="mt-2 text-xs font-semibold text-n-blue-11 hover:text-n-blue-12"
          :aria-expanded="expandedTransferRequestId === request.id"
          @click="toggleTransferRequestDetails(request.id)"
        >
          {{
            expandedTransferRequestId === request.id
              ? 'Ocultar detalhes'
              : 'Ver detalhes'
          }}
        </button>

        <div
          v-if="expandedTransferRequestId === request.id"
          class="mt-2 max-h-64 space-y-1.5 overflow-y-auto rounded-md bg-n-alpha-1 p-2 text-xs text-n-slate-11"
        >
          <p class="break-words">
            <span class="font-semibold">Solicitante:</span>
            {{ request.requester?.name || 'Não informado' }}
          </p>
          <p class="break-words">
            <span class="font-semibold">Dono atual:</span>
            {{ request.current_owner?.name || 'Sem dono definido' }}
          </p>
          <p class="break-words">
            <span class="font-semibold">Novo dono:</span>
            {{ request.target_assignee?.name || 'Não informado' }}
          </p>
          <p v-if="request.team_name" class="break-words">
            <span class="font-semibold">Equipe:</span>
            {{ request.team_name }}
          </p>
          <p>
            <span class="font-semibold">Solicitado em:</span>
            {{ formatTransferRequestDate(request.created_at) }}
          </p>
          <p class="whitespace-pre-wrap break-words">
            <span class="font-semibold">Justificativa:</span>
            {{ request.justification }}
          </p>
        </div>

        <div class="mt-2 flex items-center gap-2">
          <button
            type="button"
            class="h-8 rounded-md bg-n-teal-9 px-3 text-xs font-semibold text-white hover:bg-n-teal-10 disabled:cursor-not-allowed disabled:opacity-60"
            :disabled="decidingTransferRequestId === request.id"
            @click="approveTransferRequest(request)"
          >
            {{
              decidingTransferRequestId === request.id
                ? 'Processando…'
                : 'Aprovar'
            }}
          </button>
          <button
            type="button"
            class="h-8 rounded-md border border-n-weak px-3 text-xs font-semibold text-n-slate-11 hover:bg-n-alpha-1 disabled:cursor-not-allowed disabled:opacity-60"
            :disabled="decidingTransferRequestId === request.id"
            @click="openRejectTransferRequestModal(request)"
          >
            Rejeitar
          </button>
        </div>
      </article>
    </div>
  </section>

  <Teleport to="body">
    <div
      v-if="rejectionTransferRequest"
      data-testid="portfolio-transfer-rejection-modal"
      class="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-4"
      @click.self="closeRejectTransferRequestModal"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="portfolio-transfer-rejection-title"
        class="max-h-[calc(100dvh-2rem)] w-full max-w-lg overflow-y-auto rounded-xl bg-n-solid-1 p-5 shadow-xl"
      >
        <div class="flex items-start justify-between gap-4">
          <div>
            <h2
              id="portfolio-transfer-rejection-title"
              class="text-base font-semibold text-n-slate-12"
            >
              Rejeitar alteração de carteirização
            </h2>
            <p class="mt-1 text-sm text-n-slate-10">
              A solicitação da conversa #{{
                rejectionTransferRequest.conversation_id
              }}
              será rejeitada.
            </p>
          </div>
          <button
            type="button"
            class="flex size-8 items-center justify-center rounded-md text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12 disabled:cursor-not-allowed disabled:opacity-60"
            aria-label="Fechar"
            :disabled="Boolean(decidingTransferRequestId)"
            @click="closeRejectTransferRequestModal"
          >
            <span class="i-lucide-x size-4" />
          </button>
        </div>

        <label
          class="mt-5 block text-sm font-medium text-n-slate-12"
          for="portfolio-transfer-rejection-reason"
        >
          Motivo da rejeição
          <span class="font-normal text-n-slate-9">(opcional)</span>
        </label>
        <textarea
          id="portfolio-transfer-rejection-reason"
          v-model="rejectionReason"
          rows="4"
          maxlength="500"
          placeholder="Explique o motivo da rejeição, se desejar."
          class="mt-2 w-full resize-none rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 focus:border-n-blue-8 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
          :disabled="Boolean(decidingTransferRequestId)"
        />

        <div class="mt-5 flex justify-end gap-2">
          <button
            type="button"
            class="h-9 rounded-lg border border-n-weak px-4 text-sm font-medium text-n-slate-11 hover:bg-n-alpha-1 disabled:cursor-not-allowed disabled:opacity-60"
            :disabled="Boolean(decidingTransferRequestId)"
            @click="closeRejectTransferRequestModal"
          >
            Cancelar
          </button>
          <button
            type="button"
            class="h-9 rounded-lg bg-n-ruby-9 px-4 text-sm font-semibold text-white hover:bg-n-ruby-10 disabled:cursor-not-allowed disabled:opacity-60"
            :disabled="Boolean(decidingTransferRequestId)"
            @click="rejectTransferRequest"
          >
            {{
              decidingTransferRequestId ? 'Rejeitando…' : 'Confirmar rejeição'
            }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>
